import PropTypes from 'prop-types';
import React, { useState } from 'react';
import SlidingPane from 'react-sliding-pane';

const ProcessingJobsSidebar = ({
  isOpen,
  onClose,
  processingJobs,
  onItemClick,
  onDeleteJob,
  team,
  csrfToken
}) => {
  const teamId = team?.id;
  const [selectedSheets, setSelectedSheets] = useState({});
  const [submittingJobId, setSubmittingJobId] = useState(null);
  const [deletingJobId, setDeletingJobId] = useState(null);

  const pendingJobs = processingJobs.filter(j => j.status === 'pending');
  const processingJobsList = processingJobs.filter(j => j.status === 'processing');
  const awaitingSelectionJobs = processingJobs.filter(j => j.status === 'awaiting_sheet_selection');
  const completedJobs = processingJobs.filter(j => j.status === 'completed');
  const failedJobs = processingJobs.filter(j => j.status === 'failed');

  const getFileStatusIcon = (status) => {
    switch (status) {
      case 'processed':
        return { icon: 'fal fa-check', color: '#10b981' };
      case 'processing':
        return { icon: 'fal fa-spinner-third fa-spin', color: 'var(--od-c-1982c4, #1982c4)' };
      default:
        return { icon: 'fal fa-clock', color: 'var(--od-c-717680, #717680)' };
    }
  };

  const handleItemClick = (item) => {
    if (onItemClick) {
      onItemClick({
        id: item.id,
        type: item.type,
        date: item.start_at,
        label: item.label,
        lane: item.lane
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const toggleSheetSelection = (jobId, sheetName) => {
    setSelectedSheets(prev => {
      const jobSheets = prev[jobId] || [];
      if (jobSheets.includes(sheetName)) {
        return { ...prev, [jobId]: jobSheets.filter(s => s !== sheetName) };
      } else {
        return { ...prev, [jobId]: [...jobSheets, sheetName] };
      }
    });
  };

  const handleSubmitSheetSelection = async (job) => {
    const sheets = selectedSheets[job.id] || [];
    if (sheets.length === 0) return;

    setSubmittingJobId(job.id);
    try {
      const response = await fetch(`/teams/${teamId}/document_processing_jobs/${job.id}/select_sheets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ sheet_names: sheets })
      });

      if (!response.ok) {
        const data = await response.json();
        alert(data.errors?.join(', ') || 'Failed to submit sheet selection');
      }
    } catch (error) {
      console.error('Error submitting sheet selection:', error);
      alert('Failed to submit sheet selection');
    } finally {
      setSubmittingJobId(null);
    }
  };

  const handleDeleteJob = async (job) => {
    if (!confirm('Remove this processing job?')) return;

    setDeletingJobId(job.id);
    try {
      const response = await fetch(`/teams/${teamId}/document_processing_jobs/${job.id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (response.ok) {
        if (onDeleteJob) {
          onDeleteJob(job.id);
        }
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.errors?.join(', ') || 'Failed to delete job');
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('Failed to delete job');
    } finally {
      setDeletingJobId(null);
    }
  };

  const renderFileRow = (file, createdItem) => {
    const statusInfo = getFileStatusIcon(file.status);

    return (
      <tr key={file.id} style={{ borderBottom: '1px solid var(--od-c-f0f0f0, #f0f0f0)' }}>
        <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className={statusInfo.icon} style={{ color: statusInfo.color, fontSize: '14px', width: '16px' }} />
            <span style={{
              fontSize: '13px',
              color: 'var(--od-c-333333, #333)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '200px'
            }}>
              {file.filename}
            </span>
          </div>
        </td>
        <td style={{ padding: '10px 12px', verticalAlign: 'middle', textAlign: 'right' }}>
          {createdItem ? (
            <button
              onClick={() => handleItemClick(createdItem)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#0369a1',
                fontWeight: 500
              }}
            >
              <i className="fal fa-arrow-right" style={{ fontSize: '10px' }} />
              {createdItem.label ? (
                <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {createdItem.label}
                </span>
              ) : (
                'View'
              )}
            </button>
          ) : file.status === 'processing' ? (
            <span style={{ fontSize: '12px', color: 'var(--od-c-717680, #717680)' }}>Processing...</span>
          ) : null}
        </td>
      </tr>
    );
  };

  const renderJobItem = (job) => {
    const statusConfig = {
      pending: {
        icon: 'fal fa-clock',
        color: 'var(--od-c-717680, #717680)',
        bgColor: 'var(--od-c-f5f5f5, #f5f5f5)',
        label: 'Queued'
      },
      processing: {
        icon: 'fal fa-spinner-third fa-spin',
        color: 'var(--od-c-1982c4, #1982c4)',
        bgColor: '#e6f3fa',
        label: 'Processing'
      },
      awaiting_sheet_selection: {
        icon: 'fal fa-file-spreadsheet',
        color: '#f59e0b',
        bgColor: '#fef3c7',
        label: 'Select Sheets'
      },
      completed: {
        icon: 'fal fa-check-circle',
        color: '#10b981',
        bgColor: '#d1fae5',
        label: 'Completed'
      },
      failed: {
        icon: 'fal fa-exclamation-circle',
        color: '#ef4444',
        bgColor: '#fee2e2',
        label: 'Failed'
      }
    };

    const config = statusConfig[job.status] || statusConfig.pending;
    const files = job.files || job.filenames?.map((f, i) => ({ id: i, filename: f, status: job.status === 'completed' ? 'processed' : job.status })) || [];
    const createdItems = job.created_items || [];
    const fileCount = job.file_count || files.length || 1;

    const getCreatedItemForFile = (file) => {
      return createdItems.find(item => item.document_upload_id === file.id);
    };

    return (
      <div
        key={job.id}
        style={{
          borderBottom: '1px solid var(--od-c-e9eaeb, #e9eaeb)',
          backgroundColor: 'var(--od-c-ffffff, #ffffff)'
        }}
      >
        <div style={{
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'var(--od-c-fafafa, #fafafa)',
          borderBottom: files.length > 0 ? '1px solid var(--od-c-e9eaeb, #e9eaeb)' : 'none'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                backgroundColor: config.bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i className={config.icon} style={{ fontSize: '16px', color: config.color }} />
            </div>
            <div>
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--od-c-333333, #333)'
              }}>
                {fileCount} file{fileCount !== 1 ? 's' : ''}
              </div>
              <div style={{
                fontSize: '11px',
                color: config.color,
                fontWeight: 500
              }}>
                {config.label}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {createdItems.length > 0 && (
              <div style={{
                fontSize: '12px',
                color: '#10b981',
                fontWeight: 500
              }}>
                {createdItems.length} item{createdItems.length !== 1 ? 's' : ''} created
              </div>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteJob(job);
              }}
              disabled={deletingJobId === job.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                background: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: deletingJobId === job.id ? 'not-allowed' : 'pointer',
                color: '#9ca3af',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                if (deletingJobId !== job.id) {
                  e.currentTarget.style.borderColor = '#fca5a5';
                  e.currentTarget.style.color = '#ef4444';
                  e.currentTarget.style.background = '#fef2f2';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.color = '#9ca3af';
                e.currentTarget.style.background = 'transparent';
              }}
              title="Remove job"
            >
              {deletingJobId === job.id ? (
                <i className="fal fa-spinner-third fa-spin" style={{ fontSize: '12px' }} />
              ) : (
                <i className="fal fa-times" style={{ fontSize: '12px' }} />
              )}
            </button>
          </div>
        </div>

        {files.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {files.map(file => renderFileRow(file, getCreatedItemForFile(file)))}
            </tbody>
          </table>
        )}

        {job.status === 'awaiting_sheet_selection' && job.available_sheets && (
          <div style={{ padding: '12px 16px' }}>
            <div style={{ fontSize: '13px', color: '#374151', marginBottom: '12px' }}>
              Select the sheets you want to import:
            </div>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {job.available_sheets.map(sheet => {
                const isSelected = (selectedSheets[job.id] || []).includes(sheet.name);
                return (
                  <label
                    key={sheet.name}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '10px 12px',
                      marginBottom: '8px',
                      backgroundColor: isSelected ? '#fef3c7' : '#f9fafb',
                      border: isSelected ? '1px solid #f59e0b' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                    onClick={() => toggleSheetSelection(job.id, sheet.name)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      style={{ marginTop: '2px' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>
                        {sheet.name}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                        {sheet.row_count} rows, {sheet.col_count} columns
                      </div>
                      {sheet.preview && sheet.preview.length > 0 && (
                        <div style={{
                          marginTop: '8px',
                          fontSize: '10px',
                          fontFamily: 'monospace',
                          color: '#6b7280',
                          backgroundColor: '#f3f4f6',
                          padding: '6px 8px',
                          borderRadius: '4px',
                          overflowX: 'auto'
                        }}>
                          {sheet.preview.slice(0, 2).map((row, i) => (
                            <div key={i} style={{ whiteSpace: 'nowrap' }}>
                              {row.slice(0, 6).join(' | ')}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
            <button
              onClick={() => handleSubmitSheetSelection(job)}
              disabled={!(selectedSheets[job.id]?.length > 0) || submittingJobId === job.id}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '10px 16px',
                backgroundColor: (selectedSheets[job.id]?.length > 0) ? '#f59e0b' : '#d1d5db',
                color: 'var(--od-c-ffffff, #ffffff)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: (selectedSheets[job.id]?.length > 0) ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {submittingJobId === job.id ? (
                <>
                  <i className="fal fa-spinner-third fa-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="fal fa-file-import" />
                  Import {selectedSheets[job.id]?.length || 0} Sheet{(selectedSheets[job.id]?.length || 0) !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        )}

        {job.status === 'failed' && job.error_message && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            fontSize: '12px',
            color: '#dc2626',
            lineHeight: 1.5
          }}>
            {job.error_message}
          </div>
        )}

        {job.status === 'completed' && createdItems.length > 0 && !files.some(f => getCreatedItemForFile(f)) && (
          <div style={{ padding: '8px 16px 12px' }}>
            <div style={{ fontSize: '11px', color: 'var(--od-c-717680, #717680)', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>
              Created Items
            </div>
            {createdItems.map(item => (
              <button
                key={`${item.type}-${item.id}`}
                onClick={() => handleItemClick(item)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '8px 12px',
                  marginBottom: '4px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <i className="fal fa-calendar-day" style={{ color: '#64748b', fontSize: '14px' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px',
                    color: '#334155',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.label}
                  </div>
                  {item.start_at && (
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {formatDate(item.start_at)}
                    </div>
                  )}
                </div>
                <i className="fal fa-chevron-right" style={{ color: '#94a3b8', fontSize: '12px' }} />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const sectionHeaderStyle = {
    padding: '14px 16px 10px',
    borderBottom: '1px solid var(--od-c-e9eaeb, #e9eaeb)',
    backgroundColor: 'var(--od-c-ffffff, #ffffff)'
  };

  const sectionTitleStyle = {
    margin: 0,
    fontFamily: "'Open Sans', sans-serif",
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '16px',
    color: 'var(--od-c-717680, #717680)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  return (
    <SlidingPane
      isOpen={isOpen}
      title="Processing Jobs"
      from="right"
      width="520px"
      onRequestClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {awaitingSelectionJobs.length > 0 && (
            <div>
              <div style={sectionHeaderStyle}>
                <h6 style={sectionTitleStyle}>
                  Needs Attention ({awaitingSelectionJobs.length})
                </h6>
              </div>
              {awaitingSelectionJobs.map(job => renderJobItem(job))}
            </div>
          )}

          {(pendingJobs.length > 0 || processingJobsList.length > 0) && (
            <div>
              <div style={sectionHeaderStyle}>
                <h6 style={sectionTitleStyle}>
                  In Progress ({pendingJobs.length + processingJobsList.length})
                </h6>
              </div>
              {processingJobsList.map(job => renderJobItem(job))}
              {pendingJobs.map(job => renderJobItem(job))}
            </div>
          )}

          {completedJobs.length > 0 && (
            <div>
              <div style={sectionHeaderStyle}>
                <h6 style={sectionTitleStyle}>
                  Completed ({completedJobs.length})
                </h6>
              </div>
              {completedJobs.map(job => renderJobItem(job))}
            </div>
          )}

          {failedJobs.length > 0 && (
            <div>
              <div style={sectionHeaderStyle}>
                <h6 style={sectionTitleStyle}>
                  Failed ({failedJobs.length})
                </h6>
              </div>
              {failedJobs.map(job => renderJobItem(job))}
            </div>
          )}

          {processingJobs.length === 0 && (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
              <i
                className="fal fa-inbox"
                style={{
                  fontSize: '48px',
                  color: 'var(--od-c-d5d7da, #d5d7da)',
                  marginBottom: '16px',
                  display: 'block'
                }}
              />
              <p style={{
                fontFamily: "'Open Sans', sans-serif",
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '24px',
                color: 'var(--od-c-717680, #717680)',
                margin: 0
              }}>
                No processing jobs
              </p>
              <p style={{
                fontFamily: "'Open Sans', sans-serif",
                fontSize: '13px',
                lineHeight: '20px',
                color: '#a1a1aa',
                margin: '8px 0 0 0'
              }}>
                Drag &amp; drop files onto the grid to process them
              </p>
            </div>
          )}
        </div>
      </div>
    </SlidingPane>
  );
};

ProcessingJobsSidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  processingJobs: PropTypes.array,
  onItemClick: PropTypes.func,
  onDeleteJob: PropTypes.func,
  team: PropTypes.object,
  csrfToken: PropTypes.string
};

ProcessingJobsSidebar.defaultProps = {
  processingJobs: [],
  onItemClick: null,
  onDeleteJob: null,
  team: null,
  csrfToken: ''
};

export default ProcessingJobsSidebar;
